import { Button, Header, Icon, Input, Loader, Select, Tooltip } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import useFeatureFlag from 'helpers/hooks/useFeatureFlag';
import useFetch from 'helpers/hooks/useFetch';
import { CompanyFilterDTO } from 'models';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COMPANY_MANAGER, cnpjMask } from 'resources';
import { getReportCompany } from 'resources/api/usermanager_api/getReport';


const AdminTabManager: React.FC = () => {
    const { t } = useTranslation(['usermanager', 'common']);
    const isFeatureHSWLEnabled = useFeatureFlag('HSLWeb');

    const [reset, setReset] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsByPage, setItemsByPage] = useState(5);
    const [companyIds, setCompanyIds] = useState<number[]>();
    const [isReportLoading, setIsReportLoading] = useState(false);

    const [companyFilter, getCompanies, loadingData, setFilter] = useFetch<CompanyFilterDTO, CompanyFilterDTO>({
        url: COMPANY_MANAGER.getCompanies,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const [countries, getCountries] = useFetch<string[], string[]>({
        url: COMPANY_MANAGER.getCountries,
        method: 'GET',
        onError: 'LoadInfo'
    });

    const navigate = useNavigate();

    const itemsPerPage = ['5', '10', '15', '20', '100'];

    useEffect(() => {
        handleData();
    }, [reset]);

    useEffect(() => {
        if (countries == null) getCountries();
    }, []);

    useEffect(() => {
        setCurrentPage(companyFilter?.data?.indice ?? 1);
    }, [getCompanies]);

    const handleData = async (indice = 1): Promise<void> => {
        await getCompanies({ ...companyFilter?.data, indice, itemByPage: itemsByPage });
    };

    useEffect(() => {
        const ids =
            companyFilter?.data?.companies
                ?.map((c) => c?.id)                             // [number | undefined]
                .filter((id): id is number => typeof id === 'number') // garante só number
            ?? [];
        setCompanyIds(ids);
    }, [companyFilter, setCompanyIds])

    const handleReport = async (): Promise<void> => {
        setIsReportLoading(true);
        try {
            const response = await getReportCompany(companyIds!); // axios.Response<Blob>
            const blob = response.data; // Blob
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Relatorio_UsersCompany_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
        } finally {
            setIsReportLoading(false);
        }
    };

    return (
        <>
            {(loadingData && <Loader text={t('common:actions.Loading')} />) || (
                <>
                    <Header title={t('dashboard:admin_tools.user_manager.name')} logo={false} menu={false}>
                        <Button
                            type="primary"
                            size="small"
                            iconPosition="right"
                            icon={<Icon name="add" size="medium" root="building" />}
                            badge="20"
                            onClick={() => navigate('new-company')}
                            content={t('common:actions.Change') + "/" + t('usermanager:translation.NewCompany')}
                        ></Button>
                    </Header>
                    <div className="form4Columns">
                        <Input
                            label={'ID'}
                            placeholder={'ID'}
                            type="number"
                            value={companyFilter?.data?.id?.toString() ?? ''}
                            onChange={(value: string) => setFilter(old => ({ ...old, id: parseInt(value) }))}
                        ></Input>
                        <Input
                            label={t('user:info.Name')}
                            placeholder={t('user:info.Name')}
                            value={companyFilter?.data?.name?.toString() ?? ''}
                            onChange={(value: string) => setFilter(old => ({ ...old, name: value }))}
                        ></Input>
                        <Input
                            label={t('user:info.EmailUser')}
                            placeholder={t('user:info.EmailUser')}
                            value={companyFilter?.data?.emailUser?.toString() ?? ''}
                            onChange={(value: string) => setFilter(old => ({ ...old, emailUser: value }))}
                        ></Input>
                        {isFeatureHSWLEnabled ? (
                            <Input
                                label={t('user:company.IdSap')}
                                placeholder={t('user:company.IdSap')}
                                value={companyFilter?.data?.idSap?.toString() ?? ''}
                                onChange={(value: string) => setFilter(old => ({ ...old, idSap: value }))}
                            ></Input>
                        ) : (
                            <Input
                                label={t('user:company.Id')}
                                placeholder={t('user:company.Id')}
                                value={cnpjMask(companyFilter?.data?.cnpj?.toString() ?? '')}
                                onChange={(value: string) => setFilter(old => ({ ...old, cnpj: cnpjMask(value) }))}
                            ></Input>
                        )}
                        {isFeatureHSWLEnabled ? (
                            <Select
                                className="SelectStyle"
                                label={t('user:address.Country')}
                                placeholder={t('user:address.Country')}
                                value={companyFilter?.data?.country?.toString() ?? ''}
                                options={(countries?.data ?? []).map((country: string) => ({
                                    text: country,
                                    value: country
                                }))}
                                onChange={(value: string) => setFilter(old => ({ ...old, country: value }))}
                            />) : null}
                        <Select
                            className="SelectStyle"
                            label={t('common:translation.ItemperPage')}
                            placeholder={t('common:translation.ItemperPage')}
                            value={itemsByPage > 50 ? '100' : itemsByPage.toString()}
                            options={itemsPerPage.map((e: string) => ({
                                text: e === '100' ? 'TODOS OS RESULTADOS' : e.toString(),
                                value: e
                            }))}
                            onChange={value => setItemsByPage(value === '100' ? companyFilter?.data?.totalItems ?? -1 : parseInt(value))}
                        />
                    </div>
                    <div className="buttonsSearch">
                        <Button
                            type="secondary"
                            icon="badge-delete"
                            content={t('common:actions.ResetFilters')}
                            iconPosition="right"
                            onClick={() => {
                                setFilter({
                                    itemByPage: 5,
                                    indice: 1,
                                    companies: companyFilter?.data?.companies,
                                    totalItems: companyFilter?.data?.totalItems
                                });
                                setItemsByPage(5);
                                setReset(old => !old);
                            }}
                        />
                        <Button
                            type="primary"
                            icon="filter1"
                            content={t('common:actions.ApplyFilters')}
                            iconPosition="right"
                            onClick={() => handleData()}
                        />
                    </div>
                    <br />
                    <DataTable
                        data={companyFilter?.data?.companies ?? []}
                        onCellClick={(data: ICellData) => navigate('company', { state: { id: data.rowData.id } })}
                    >
                        <DataTable.Column header={'ID'} field="id" />
                        <DataTable.Column header={t('user:info.Name')} field="name" />
                        {isFeatureHSWLEnabled ? (
                            <DataTable.Column header={t('user:company.IdSap')} field="idSap" />
                        ) : (
                            <DataTable.Column header={t('user:company.Id')} field="cnpj" />
                        )}
                        {isFeatureHSWLEnabled ? (
                            <DataTable.Column header={t('user:address.Country')} field="country" />
                        ) : null}
                        <DataTable.Pagination
                            disabledPages={[currentPage]}
                            activePage={currentPage}
                            itemsPerPage={companyFilter?.data?.itemByPage ?? itemsByPage}
                            totalItems={companyFilter?.data?.totalItems ?? 0}
                            onPageChange={(page: any) => {
                                handleData(page);
                            }}
                        ></DataTable.Pagination>

                    </DataTable>
                    <br />
                    <div className="buttonsSearch">
                        {isReportLoading ?
                            <Button
                                type="primary"
                                icon="refresh"
                                loading={true}
                                disabled={isReportLoading}
                                content={t('common:actions.Loading')}
                                iconPosition="right"
                            /> :
                            <div>
                                <Tooltip
                                    className="tooltip-justified"
                                    element={
                                        <Button
                                            type="primary"
                                            icon="export"
                                            disabled={isReportLoading}
                                            content={t('common:actions.Export')}
                                            iconPosition="right"
                                            onClick={() => handleReport()}
                                        />
                                    }
                                    content={(companyFilter?.data?.companies?.length ?? 0) + " Companhias serão exportadas!"}
                                    position="top center"
                                    size="small"
                                />
                            </div>
                        }
                    </div>
                </>
            )}
        </>
    );
};

export default AdminTabManager;
